import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Sistem notifikasi sementara.
 *
 * Wadahnya memakai `role="status"` dengan `aria-live="polite"`, sehingga
 * pembaca layar mengumumkan pesan begitu muncul tanpa memotong apa yang
 * sedang dibaca pengguna. Toast galat tidak menghilang sendiri - pesan
 * kegagalan yang lenyap sebelum sempat dibaca adalah kegagalan kedua.
 */

const ToastContext = createContext(null);

const ICONS = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const STYLES = {
  success: 'border-success-200 bg-success-50 text-success-700 dark:border-success-500/25 dark:bg-success-500/10 dark:text-success-500',
  error: 'border-danger-200 bg-danger-50 text-danger-700 dark:border-danger-500/25 dark:bg-danger-500/10 dark:text-danger-500',
  warning: 'border-warning-200 bg-warning-50 text-warning-700 dark:border-warning-500/25 dark:bg-warning-500/10 dark:text-warning-500',
  info: 'border-info-200 bg-info-50 text-info-700 dark:border-info-500/25 dark:bg-info-500/10 dark:text-info-500',
};

let nextId = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((toast) => {
    const id = ++nextId;
    // Semuanya hilang sendiri setelah 5 detik, galat sekalipun.
    //
    // Dulu galat sengaja menetap sampai ditutup manual, supaya pesannya
    // sempat terbaca. Yang terjadi di pemakaian sehari-hari justru
    // sebaliknya: pemberitahuan gagal menumpuk di sudut layar dan tidak
    // pernah pergi, sampai pengguna berhenti membacanya sama sekali.
    //
    // Yang menggantikannya ada di ToastItem: hitungan mundur BERHENTI selama
    // penunjuk berada di atas pemberitahuannya. Yang perlu membaca lama
    // tinggal mengarahkan kursor, dan yang tidak membaca tidak perlu
    // membersihkan layarnya sendiri.
    const duration = toast.duration ?? 5000;
    setToasts((current) => [...current, { ...toast, id, duration }].slice(-4));
    return id;
  }, []);

  const api = useMemo(
    () => ({
      push,
      dismiss,
      success: (title, options = {}) => push({ type: 'success', title, ...options }),
      error: (title, options = {}) => push({ type: 'error', title, ...options }),
      warning: (title, options = {}) => push({ type: 'warning', title, ...options }),
      info: (title, options = {}) => push({ type: 'info', title, ...options }),
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-0 sm:bottom-0 sm:items-end"
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const ToastItem = ({ toast, onDismiss }) => {
  const Icon = ICONS[toast.type] ?? Info;
  const [tertahan, setTertahan] = useState(false);

  useEffect(() => {
    if (!toast.duration || tertahan) return undefined;
    const timer = setTimeout(() => onDismiss(toast.id), toast.duration);
    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, tertahan, onDismiss]);

  return (
    <div
      // Hitungan mundur berhenti selama penunjuk atau fokus papan ketik
      // berada di sini. Pesan galat yang panjang jadi bisa dibaca setenang
      // yang diperlukan tanpa membuat pemberitahuan menetap bagi semua orang.
      onMouseEnter={() => setTertahan(true)}
      onMouseLeave={() => setTertahan(false)}
      onFocus={() => setTertahan(true)}
      onBlur={() => setTertahan(false)}
      className={cn(
        'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border p-3.5 shadow-lg',
        'animate-in',
        STYLES[toast.type] ?? STYLES.info,
      )}
    >
      <Icon className="mt-0.5 size-[18px] shrink-0" aria-hidden="true" />

      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{toast.title}</p>
        {toast.description && (
          <p className="mt-0.5 text-[13px] leading-relaxed opacity-90">{toast.description}</p>
        )}
        {toast.action && <div className="mt-2">{toast.action}</div>}
      </div>

      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Tutup notifikasi"
        className="-m-1 shrink-0 rounded-md p-1 opacity-60 transition-opacity hover:opacity-100"
      >
        <X className="size-4" />
      </button>
    </div>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast harus dipakai di dalam <ToastProvider>.');
  return context;
};
